import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useExperiment } from '@web/experiments';
import {
  BaseHeader,
  LeftMenuLinks,
  CenterMenuLinks,
  RotatingPromoBar,
  UserMenuLinks,
} from '@web/molecules';
import { useSessionCustomer } from '../customer';
import { MarketingHeaderWrapper } from './MarketingHeader.styles.js';
import { schema as rotatingPromoBarSchema } from './builders/rotating_promo_bar';

export const MarketingHeader = ({
  mobileLinks,
  links,
  logoUrl,
  logoSrText,
  sticky = true,
  hidePromoBar,
  rotatingPromoBarProps,
  ctaButton,
  mobileCtaButton,
  logoImg,
  bgColor = 'background.dark',
  menuBgColor,
  fontColor = 'text.lightest',
  hideLoginLinkMobile,
  hideLoginLinkDesktop = false,
  mobileLogo,
  closeButtonPosition = 'left',
  closeButtonIconColor,
  closeButtonBackgroundColor,
  closeButtonBorderColor,
  fullBleedNav = false,
  fullBleedLogo = null,
}) => {
  const { isActive } = useSessionCustomer();
  const { value: ctaExperimentValue = 'control' } = useExperiment(
    ctaButton?.[0]?.testName || null,
  );
  const { value: mobileCtaExperimentValue = 'control' } = useExperiment(
    mobileCtaButton?.[0]?.testName || null,
  );
  const { value: mobileLinkExperimentValue = 'control' } = useExperiment(
    mobileLinks?.testName || null,
  );
  const { value: promoBarExperimentValue = 'control' } = useExperiment(
    rotatingPromoBarProps?.test_name ?? null,
  );

  const mobileExperimentLinks = useMemo(() => {
    if (mobileLinks?.testName) {
      const experimentVariation = mobileLinks?.variants?.find(
        variant => variant.variant_name === mobileLinkExperimentValue,
      );
      return experimentVariation?.blocks?.[0]?.navigation_links || mobileLinks;
    }
    return mobileLinks;
  }, [mobileLinks, mobileLinkExperimentValue]);

  const { value: linkExperimentValue = 'control' } = useExperiment(
    links?.testName || null,
  );

  const experimentLinks = useMemo(() => {
    if (links?.testName) {
      const experimentVariation = links?.variants?.find(
        variant => variant.variant_name === linkExperimentValue,
      );
      return experimentVariation?.blocks?.[0]?.navigation_links || links;
    }
    return links;
  }, [links, linkExperimentValue]);

  const experimentCtaButton = useMemo(() => {
    if (ctaButton?.[0]?.component === 'flagship_ab_test') {
      const experimentVariation = ctaButton?.[0]?.variants?.find(
        variant => variant.variant_name === ctaExperimentValue,
      );
      const variantButtons = (experimentVariation?.blocks || []).map(
        button => ({
          mobileCTAText: button.mobileCTAText || button.mobile_cta_text,
          desktopCTAText: button.desktopCTAText || button.desktop_cta_text,
          ctaTarget:
            button.ctaTarget ||
            button.cta_target?.cached_url ||
            button.cta_target?.url,
          ctaVariant: button.ctaVariant || button.cta_variant,
          headerText: button.headerText || button.header_text,
          component: button.component,
        }),
      );

      return variantButtons || ctaButton;
    }

    return ctaButton;
  }, [ctaButton, ctaExperimentValue]);

  const mobileExperimentCtaButton = useMemo(() => {
    if (mobileCtaButton?.[0]?.component === 'flagship_ab_test') {
      const experimentVariation = mobileCtaButton?.[0]?.variants?.find(
        variant => variant.variant_name === mobileCtaExperimentValue,
      );
      const variantButtons = (experimentVariation?.blocks || []).map(
        button => ({
          mobileCTAText: button.mobileCTAText || button.mobile_cta_text,
          desktopCTAText: button.desktopCTAText || button.desktop_cta_text,
          ctaTarget:
            button.ctaTarget ||
            button.cta_target?.cached_url ||
            button.cta_target?.url,
          ctaVariant: button.ctaVariant || button.cta_variant,
          headerText: button.headerText || button.header_text,
          component: button.component,
        }),
      );

      return variantButtons || mobileCtaButton;
    }

    return mobileCtaButton;
  }, [mobileCtaButton, mobileCtaExperimentValue]);

  const resolvedRotatingPromoBarProps = useMemo(() => {
    if (!rotatingPromoBarProps) return null;
    if (rotatingPromoBarProps.component === 'flagship_ab_test') {
      const raw =
        rotatingPromoBarProps.variants?.find(
          v => v.variant_name === promoBarExperimentValue,
        )?.blocks?.[0] ?? null;
      if (!raw) return null;
      return rotatingPromoBarSchema.cast(raw);
    }
    return rotatingPromoBarProps;
  }, [rotatingPromoBarProps, promoBarExperimentValue]);

  const topHeaderSegment =
    !hidePromoBar && !isActive && resolvedRotatingPromoBarProps ? (
      <RotatingPromoBar {...resolvedRotatingPromoBarProps} />
    ) : null;

  const stickyBanner =
    !!topHeaderSegment && (resolvedRotatingPromoBarProps?.sticky || false);

  const rightNavSegment = (
    <UserMenuLinks
      ctas={experimentCtaButton}
      hideLoginLink={hideLoginLinkDesktop}
    />
  );
  const leftNavSegment = (
    <LeftMenuLinks
      links={mobileExperimentLinks}
      menuBgColor={menuBgColor}
      ctas={mobileExperimentCtaButton ?? experimentCtaButton}
      hideLoginLink={hideLoginLinkMobile}
      logo={mobileLogo}
      closeButtonPosition={closeButtonPosition}
      closeButtonIconColor={closeButtonIconColor}
      closeButtonBackgroundColor={closeButtonBackgroundColor}
      closeButtonBorderColor={closeButtonBorderColor}
    />
  );
  const centerNavSegment = <CenterMenuLinks links={experimentLinks} />;
  return (
    <MarketingHeaderWrapper>
      <BaseHeader
        logoImg={logoImg}
        fullBleedLogo={fullBleedLogo}
        logoUrl={logoUrl}
        logoSrText={logoSrText}
        topSegment={topHeaderSegment}
        rightSegment={rightNavSegment}
        centerSegment={centerNavSegment}
        leftSegment={leftNavSegment}
        sticky={sticky}
        stickyBanner={stickyBanner}
        hasBanner={!!topHeaderSegment}
        bgColor={bgColor}
        fontColor={fontColor}
        headerVerticalPadding={5}
        headerHorizontalPadding={3}
        fullBleedNav={fullBleedNav}
      />
    </MarketingHeaderWrapper>
  );
};

MarketingHeader.displayName = 'MarketingHeader';

MarketingHeader.propTypes = {
  links: PropTypes.array.isRequired,
  logoImg: PropTypes.object,
  logoUrl: PropTypes.string,
  logoSrText: PropTypes.string,
  sticky: PropTypes.bool,
  hidePromoBar: PropTypes.bool,
  ctaButton: PropTypes.array,
  testName: PropTypes.string,
  bgColor: PropTypes.string,
  fontColor: PropTypes.string,
  menuBgColor: PropTypes.string,
  hideLoginLinkMobile: PropTypes.bool,
  hideLoginLinkDesktop: PropTypes.bool,
  mobileLogo: PropTypes.object,
  closeButtonPosition: PropTypes.oneOf(['left', 'right']),
  closeButtonIconColor: PropTypes.string,
  closeButtonBackgroundColor: PropTypes.string,
  closeButtonBorderColor: PropTypes.string,
  fullBleedNav: PropTypes.bool,
  fullBleedLogo: PropTypes.object,
};
